import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";

import {Calendar} from "primereact/calendar";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";

import appStore from "../../../storage/AppStore";
import clubStore from "../../../storage/ClubStore";
import systemStore from "../../../storage/SystemStore";

import Btn from "../../../ui/Btn";
import Select from "../../../ui/Select";
import PageLayout from "../../PageLayout";

import "../../../common/billing.css";

const BILLING_TRAINING = 1;
const BILLING_TOURNAMENT = 2;
const BILLING_REFEREE = 3;
const BILLING_EQUIPMENT = 4;
const BILLING_TEAM_BUILDING = 5;
const BILLING_OTHER = 6;

const TYPE_ICONS = {
    [BILLING_TRAINING]: "pi pi-calendar",
    [BILLING_TOURNAMENT]: "pi pi-trophy",
    [BILLING_REFEREE]: "pi pi-stopwatch",
    [BILLING_EQUIPMENT]: "pi pi-shopping-bag",
    [BILLING_TEAM_BUILDING]: "pi pi-users",
    [BILLING_OTHER]: "pi pi-ellipsis-h",
};

const parseDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return value;
    }

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
    }

    // DD.MM.YYYY
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        const [day, month, year] = value.split(".").map(Number);
        return new Date(year, month - 1, day);
    }

    return null;
};

const NewBilling = () => {
    const navigate = useNavigate();

    const [billingTypeId, setBillingTypeId] = useState(0);
    const [objectId, setObjectId] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    const [name, setName] = useState("");
    const [amount, setAmount] = useState(0);

    const [periodFrom, setPeriodFrom] = useState(null);
    const [periodTo, setPeriodTo] = useState(null);

    const [selectedIds, setSelectedIds] = useState([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (clubStore.billing_types.length === 0) {
            clubStore.getBillingTypes();
        }

        clubStore.billing.resetCreateOptions();

        return () => {
            clubStore.billing.resetCreateOptions();
        };
    }, []);

    const selectedObject = useMemo(() => {
        return clubStore.billing.objects.find(
            (item) => item.id === objectId
        );
    }, [clubStore.billing.objects, objectId]);

    const filteredMembers = useMemo(() => {
        const search = memberSearch
            .trim()
            .toLowerCase();

        if (!search) {
            return clubStore.billing.members;
        }

        return clubStore.billing.members.filter(
            (member) =>
                (member.title || "")
                    .toLowerCase()
                    .includes(search)
        );
    }, [
        clubStore.billing.members,
        memberSearch
    ]);

    const resetForm = () => {
        setObjectId(null);
        setSelectedPeriod(null);

        setName("");
        setAmount(0);

        setPeriodFrom(null);
        setPeriodTo(null);

        setSelectedIds([]);
        setMemberSearch("");

        clubStore.billing.resetCreateOptions();
    };

    const handleTypeSelect = async (typeId) => {
        resetForm();

        setBillingTypeId(typeId);

        if (
            typeId === BILLING_TRAINING ||
            typeId === BILLING_TOURNAMENT ||
            typeId === BILLING_REFEREE
        ) {
            await clubStore.billing.getObjects(typeId);
        }

        // Для всех типов кроме Training можно сразу
        // получить всех активных участников клуба.
        if (typeId !== BILLING_TRAINING) {
            await clubStore.billing.getMembers();
        }
    };

    const handleObjectSelect = async (value) => {
        setObjectId(value || null);
        setSelectedPeriod(null);
        setSelectedIds([]);

        if (!value) {
            setName("");
            setAmount(0);
            return;
        }

        const object = clubStore.billing.objects.find(
            (item) => item.id === value
        );

        if (!object) {
            return;
        }

        setAmount(Number(object.fee || 0));

        // TRAINING

        if (billingTypeId === BILLING_TRAINING) {
            setName("");
            setPeriodFrom(null);
            setPeriodTo(null);

            await clubStore.billing.getTrainingPeriods(
                object.id
            );

            return;
        }

        // TOURNAMENT / GAME

        setName(object.name || "");

        if (object.period_from) {
            setPeriodFrom(
                parseDate(object.period_from)
            );
        }

        if (object.period_to) {
            setPeriodTo(
                parseDate(object.period_to)
            );
        }
    };

    const handleTrainingPeriod = async (period) => {
        setSelectedPeriod(period);
        setSelectedIds([]);

        if (!period) {
            setPeriodFrom(null);
            setPeriodTo(null);
            setName("");
            return;
        }

        setPeriodFrom(
            parseDate(period.period_from)
        );

        setPeriodTo(
            parseDate(period.period_to)
        );

        setAmount(
            Number(
                period.amount ||
                selectedObject?.fee ||
                0
            )
        );

        setName(period.name)

        await clubStore.billing.getMembers({
            team_id: objectId,
            billing_group_id:
                period.billing_group_id || null
        });
    };

    const toggleMember = (companyUserId) => {
        setSelectedIds((current) => {
            if (current.includes(companyUserId)) {
                return current.filter(
                    (id) => id !== companyUserId
                );
            }

            return [
                ...current,
                companyUserId
            ];
        });
    };

    const selectAll = () => {
        const ids = filteredMembers.map(
            (member) => member.company_user_id
        );

        setSelectedIds((current) => [
            ...new Set([
                ...current,
                ...ids
            ])
        ]);
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const canSave =
        billingTypeId > 0 &&
        name.trim().length > 0 &&
        Number(amount) > 0 &&
        periodFrom &&
        periodTo &&
        selectedIds.length > 0 &&
        (
            billingTypeId !== BILLING_TRAINING ||
            (
                objectId &&
                selectedPeriod
            )
        );

    const handleSave = async () => {
        if (!canSave || saving) {
            return;
        }

        setSaving(true);

        try {
            const res = await clubStore.billing.addGroup({
                billing_type_id: billingTypeId,
                object_id: objectId || null,

                name: name.trim(),
                amount: Number(amount),

                period_from: periodFrom,
                period_to: periodTo,

                company_user_ids: selectedIds
            });

            if (!res || res.result !== 0) {
                throw new Error(
                    "billing_create_error"
                );
            }

            systemStore.setSuccess(
                appStore.tr.successfully_saved
            );

            const groupId =
                res.billing_group?.id ||
                res.billing_group_id;

            if (groupId) {
                navigate(
                    `/manager/billing/group/${groupId}`
                );
            } else {
                navigate(
                    "/manager/billing/info"
                );
            }

        } catch (error) {
            systemStore.setError(
                appStore.tr[error.message] ||
                error.message
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageLayout
            header={
                <div className="flex justify-content-between align-items-center w-full">
                    <h3 className="m-0">
                        {appStore.tr.add_billing}
                    </h3>
                    <Btn
                        icon="pi pi-arrow-left"
                        severity="secondary"
                        outlined
                        onClick={() =>
                            navigate(
                                "/manager/billing/info"
                            )
                        }
                    />
                </div>
            }
        >
            {/* BILLING TYPE */}
            <div className="col-12">
                <div className="mb-2 font-semibold">
                    {appStore.tr.billing_type}
                </div>
                <div className="choice-grid">
                    {clubStore.billing_types.map((type) => {
                        const active =
                            billingTypeId === type.id;
                        return (
                            <button
                                type="button"
                                key={type.id}
                                className={
                                    `choice-tile ${
                                        active
                                            ? "choice-tile--active"
                                            : ""
                                    }`
                                }
                                onClick={() =>
                                    handleTypeSelect(type.id)
                                }
                            >
                                <span className="choice-tile__icon">
                                    <i
                                        className={
                                            TYPE_ICONS[type.id] ||
                                            "pi pi-money-bill"
                                        }
                                    />
                                </span>
                                <span className="choice-tile__title">
                                    {type.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            {billingTypeId > 0 && (
                <>
                    {/* OBJECT */}
                    {[
                        BILLING_TRAINING,
                        BILLING_TOURNAMENT,
                        BILLING_REFEREE
                    ].includes(billingTypeId) && (
                        <div className="col-12">
                            <label className="block mb-2">
                                {appStore.tr.payment_purpose ||
                                    "Объект"}
                            </label>
                            <Select value={objectId} options={clubStore.billing.objects}
                                    optionLabel="name" optionValue="id" filter
                                    showClear={billingTypeId !== BILLING_TRAINING}
                                    label={appStore.tr.select || "Выберите"}
                                    onChange={handleObjectSelect}/>
                        </div>
                    )}
                    {/* TRAINING PERIOD */}
                    {billingTypeId ===
                        BILLING_TRAINING &&
                        objectId && (
                            <div className="col-12">
                                <label className="block mb-2">{appStore.tr.billing_period}</label>
                                <Select value={selectedPeriod?.period_from || null}
                                        options={clubStore.billing.periods}
                                        optionLabel="name" optionValue="period_from"
                                        label={appStore.tr.select || "Выберите"}
                                        onChangeObject={(period) => handleTrainingPeriod(period || null)}
                                        itemTemplate={(period) => (
                                            <div className="flex justify-content-between w-full gap-3">
                                                <span>{period.name}</span>
                                                <span className="text-600">{period.available_count}/{period.members_count}</span>
                                            </div>
                                        )}/>
                                {selectedPeriod?.is_existing && (
                                    <div className="text-600 mt-2">
                                        {appStore.tr.billing ||
                                            "Начисление"}{" "}
                                        #{selectedPeriod.billing_group_id}
                                        {" — "}
                                        {appStore.tr.paid ||
                                            "начислено"}{" "}
                                        {
                                            selectedPeriod.billed_count
                                        }
                                        {" / "}
                                        {
                                            selectedPeriod.members_count
                                        }
                                    </div>
                                )}
                            </div>
                        )}
                    {/* NAME */}
                    {(
                        billingTypeId !== BILLING_TRAINING ||
                        selectedPeriod
                    ) && (
                        <div className="col-12">
                            <label className="block mb-2">
                                {appStore.tr.name}
                            </label>
                            <InputText
                                value={name}
                                className="w-full"
                                onChange={(e) =>
                                    setName(
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    )}
                    {/* PERIOD FOR NON TRAINING */}
                    {billingTypeId !==
                        BILLING_TRAINING && (
                        <>
                            <div className="col-6">
                                <label className="block mb-2">
                                    {appStore.tr.date_from ||
                                        "Дата с"}
                                </label>
                                <Calendar
                                    value={periodFrom}
                                    onChange={(e) =>
                                        setPeriodFrom(
                                            e.value
                                        )
                                    }
                                    dateFormat="dd.mm.yy"
                                    className="w-full"
                                    inputClassName="w-full"
                                />
                            </div>
                            <div className="col-6">
                                <label className="block mb-2">
                                    {appStore.tr.date_to ||
                                        "Дата по"}
                                </label>
                                <Calendar
                                    value={periodTo}
                                    minDate={periodFrom}
                                    onChange={(e) =>
                                        setPeriodTo(
                                            e.value
                                        )
                                    }
                                    dateFormat="dd.mm.yy"
                                    className="w-full"
                                    inputClassName="w-full"
                                />
                            </div>
                        </>
                    )}
                    {/* AMOUNT */}
                    {(
                        billingTypeId !== BILLING_TRAINING ||
                        selectedPeriod
                    ) && (
                        <div className="col-12">
                            <label className="block mb-2">
                                {appStore.tr.amount}
                            </label>
                            <InputNumber
                                value={amount}
                                min={0}
                                useGrouping={false}
                                className="w-full"
                                inputClassName="w-full"
                                onValueChange={(e) =>
                                    setAmount(
                                        e.value || 0
                                    )
                                }
                            />
                        </div>
                    )}
                    {/* MEMBERS */}
                    {(
                        billingTypeId !== BILLING_TRAINING ||
                        selectedPeriod
                    ) && (
                        <div className="col-12 mt-3">
                            <div className="game-card">
                                <div className="flex justify-content-between align-items-center gap-2 mb-3">
                                    <div>
                                        <h4 className="m-0">
                                            {appStore.tr.members ||
                                                "Участники"}
                                        </h4>
                                        <div className="text-600 mt-1">
                                            {
                                                clubStore.billing.members.length
                                            }
                                            {" "}
                                            {appStore.tr.available ||
                                                "доступно"}
                                            {selectedIds.length >
                                                0 && (
                                                <>
                                                    {" · "}
                                                    {
                                                        selectedIds.length
                                                    }
                                                    {" "}
                                                    {appStore.tr.selected ||
                                                        "выбрано"}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Btn
                                            label={
                                                appStore.tr.select_all ||
                                                "Все"
                                            }
                                            outlined
                                            severity="secondary"
                                            onClick={
                                                selectAll
                                            }
                                        />
                                        {selectedIds.length >
                                            0 && (
                                            <Btn
                                                icon="pi pi-times"
                                                outlined
                                                severity="secondary"
                                                onClick={
                                                    clearSelection
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <InputText
                                    value={memberSearch}
                                    className="w-full mb-3"
                                    placeholder={
                                        appStore.tr.search ||
                                        "Поиск"
                                    }
                                    onChange={(e) =>
                                        setMemberSearch(
                                            e.target.value
                                        )
                                    }
                                />
                                <div className="billing-member-list">
                                    {filteredMembers.map(
                                        (member) => {
                                            const selected =
                                                selectedIds.includes(
                                                    member.company_user_id
                                                );
                                            return (
                                                <div
                                                    key={
                                                        member.company_user_id
                                                    }
                                                    className="billing-member-row"
                                                    onClick={() =>
                                                        toggleMember(
                                                            member.company_user_id
                                                        )
                                                    }
                                                >
                                                    <div className="flex align-items-center gap-3">
                                                        <Checkbox
                                                            checked={
                                                                selected
                                                            }
                                                            onChange={() =>
                                                                toggleMember(
                                                                    member.company_user_id
                                                                )
                                                            }
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        />
                                                        <span>
                                                            {
                                                                member.title
                                                            }
                                                        </span>
                                                    </div>
                                                    {member.amount && (
                                                        <span className="text-600">
                                                            {
                                                                member.amount
                                                            }
                                                            {
                                                                clubStore
                                                                    .club
                                                                    .country
                                                                    ?.currency
                                                                    ?.data
                                                                    ?.sign ||
                                                                ""
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                    {filteredMembers.length ===
                                        0 && (
                                        <div className="text-center text-600 py-3">
                                            {
                                                appStore.tr
                                                    .not_found
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* SAVE */}
                    {(
                        billingTypeId !== BILLING_TRAINING ||
                        selectedPeriod
                    ) && (
                        <div className="col-12">
                            <Btn
                                icon="pi pi-save"
                                severity="success"
                                label={
                                    `${appStore.tr.add || "Добавить"}${
                                        selectedIds.length
                                            ? ` (${selectedIds.length})`
                                            : ""
                                    }`
                                }
                                className="w-full justify-content-center"
                                disabled={
                                    !canSave ||
                                    saving
                                }
                                onClick={
                                    handleSave
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </PageLayout>
    );
};
export default observer(NewBilling);